/// <reference lib="dom" />
import { useState, useCallback } from "react";
import {
  createEdgeAuth,
  type EdgeAuthClient,
  type AuthSession,
  type SessionData,
} from "@pineseed/auth";
import type { CasdoorUser } from "@pineseed/gui/types/auth";
import type { Role } from "@pineseed/spec";


// Extend CasdoorUser with role from our session
type CasdoorUserWithRole = CasdoorUser & { role: Role };



// Type for login result
export type LoginResult = {
  user: CasdoorUser;
  tokens: {
    accessToken: string;
    refreshToken?: string;
    tokenType: string;
    expiresIn: number;
    scope: string;
    idToken?: string;
  };
};

/** Injected login flow provider — decouples useAccountEdgeAuth from any specific auth handler implementation. */
export interface LoginProvider {
  getLoginUrl(state: string): Promise<string>;
  handleCallback(code: string, state: string): Promise<{ user: CasdoorUser; tokens: { accessToken: string; refreshToken?: string; tokenType: string; expiresIn: number; scope: string; idToken?: string } }>;
  clearStoredAuthState(): Promise<void>;
}

/**
 * Account-scoped edge authentication hook - Independent popup + server exchange
 *
 * SIMPLE FLOW:
 * 1. Independent popup login (same behavior as workspace atoms)
 * 2. Server's /system/auth/exchange?accountId endpoint handles JWT → account API key
 * 3. Edge auth client manages API key sessions
 *
 * Architecture:
 * - Independent popup (NO workspace state dependency)
 * - Uses server's account-scoped auth middleware (already set up)
 * - Edge auth client for session management
 *
 * @param accountId - Account the minted key is scoped to
 * @param ttlS - Requested key lifetime in seconds (capped server-side)
 */
export function useAccountEdgeAuth(
  accountId: string,
  ttlS: number,
  apiBase: string,
  loginProvider: LoginProvider,
) {
  const [edgeAuth] = useState<EdgeAuthClient>(() => createEdgeAuth(apiBase, "/system/auth"));
  const [hasValidSession, setHasValidSession] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<CasdoorUserWithRole | null>(
    null,
  );
  const [authLoading, setAuthLoading] = useState(false);

  const buildFnUrl = useCallback(
    (method: string) => {
      return `${apiBase}/system/auth/${method}`;
    },
    [apiBase],
  );

  const handleSignout = async () => {
    try {
      await edgeAuth.logout();
    } catch (error) {
      console.warn("Logout failed:", error);
    }
    setHasValidSession(false);
    setIsAuthenticated(false);
    setCurrentUser(null);
  };

  // POST /system/auth/exchange?accountId=<accountId> with body {ttl}
  const postExchange = useCallback(
    (jwt: string) =>
      fetch(
        `${apiBase}/system/auth/exchange?${new URLSearchParams({ accountId }).toString()}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ttl: ttlS }),
          credentials: "include", // ✅ Include cookies
        },
      ),
    [apiBase, accountId, ttlS],
  );

  // Exchange JWT for API key using server's /system/auth/exchange
  const exchangeJWTForApiKey = useCallback(
    async (jwt: string): Promise<boolean> => {
      try {
        console.log("🔄 Exchanging JWT for API key via server...");

        // Use server's /system/auth/exchange endpoint (account-scoped)
        const response = await postExchange(jwt);

        if (!response.ok) {
          throw new Error(
            `Server auth exchange failed: ${response.status} ${response.statusText}`,
          );
        }

        const session = (await response.json()) as AuthSession;



        // Cookie-only storage - no localStorage needed
        // Session data is automatically stored in httpOnly cookie by server

        // Update our independent state
        setHasValidSession(true);
        setIsAuthenticated(true);

        // Set user info from session with rich data
        if (session.sessionData) {
          const sessionData: SessionData = session.sessionData;
          setCurrentUser({
            name: sessionData.username,
            id: sessionData.userId,
            displayName: sessionData.displayName,
            email: sessionData.email,
            avatar: sessionData.avatar,
            role: sessionData.role as Role,
          } as CasdoorUserWithRole);
        }

        return true;
      } catch (error) {
        console.error("❌ JWT exchange failed:", error);
        setHasValidSession(false);
        setIsAuthenticated(false);
        return false;
      }
    },
    [postExchange],
  );

  // Validate existing session
  const validateSession = useCallback(async (): Promise<boolean> => {
    try {
      const isValid = await edgeAuth.validateSession();
      setHasValidSession(isValid);
      setIsAuthenticated(isValid);

      // Restore currentUser from EdgeAuthClient's session data
      if (isValid) {
        const session = edgeAuth.getSession() as AuthSession | null;
        if (session?.sessionData) {

          const sessionData: SessionData = session.sessionData;
          setCurrentUser({
            name: sessionData.username,
            id: sessionData.userId,
            displayName: sessionData.displayName,
            email: sessionData.email,
            avatar: sessionData.avatar,
            role: sessionData.role as Role,
          } as CasdoorUserWithRole);
        }
      } else {
        setCurrentUser(null);
      }
      return isValid;
    } catch (error) {
      console.warn("Session validation failed:", error);
      setHasValidSession(false);
      setIsAuthenticated(false);
      return false;
    }
  }, [edgeAuth]);

  // Independent popup login - SAME behavior as workspace atoms but NO dependency
  const loginWithPopup = useCallback(
    async (): Promise<LoginResult> => {
      try {
        setAuthLoading(true);
        console.log("🔄 Starting independent popup login...");

        // Generate cryptographic state for CSRF protection
        const stateBytes = crypto.getRandomValues(new Uint8Array(32));
        const expectedState = Array.from(stateBytes, b => b.toString(16).padStart(2, "0")).join("");
        const loginUrl = await loginProvider.getLoginUrl(expectedState);

        return new Promise<LoginResult>((resolve, reject) => {
          // Open popup (same as workspace auth handler)
          const popup = window.open(
            loginUrl,
            "CasdoorLogin",
            "width=600,height=700,scrollbars=yes,resizable=yes,status=yes,location=yes,toolbar=no,menubar=no",
          );

          if (!popup) {
            reject(
              new Error("Popup blocked. Please allow popups for this site."),
            );
            return;
          }

          // Use postMessage instead of polling to avoid COOP issues
          const handleMessage = async (event: MessageEvent) => {
            // Strict origin check — reject any message not from our exact origin
            if (event.origin !== window.location.origin) {
              return;
            }

            if (event.data?.type === "CASDOOR_AUTH_CALLBACK") {
              window.removeEventListener("message", handleMessage);
              clearTimeout(timeoutId);

              const { code, error, state } = event.data;

              try {
                // popup.close();
              } catch (e) {
                // Ignore COOP errors when closing
              }

              if (error) {
                reject(new Error(`Auth error: ${error}`));
                return;
              }

              // Validate OAuth state — CSRF protection
              if (state !== expectedState) {
                reject(new Error("OAuth state mismatch — possible CSRF attack"));
                return;
              }

              if (code) {

                try {
                  const result = await loginProvider.handleCallback(
                    code,
                    state,
                  );
                  const jwt = result.tokens.accessToken;


                  // Exchange JWT for API key using account-scoped endpoint
                  const response = await postExchange(jwt);

                  if (!response.ok) {
                    throw new Error(`JWT exchange failed: ${response.status}`);
                  }

                  // Cookie-only storage - no localStorage needed
                  // Session data is automatically stored in httpOnly cookie by server
                  const sesh = (await response.json()) as AuthSession; // Cookie automatically stored by browser
                  const role = (sesh.sessionData.role as Role) || "guest";

                  // Set user from login result (already has full user data)
                  // Note: result.user from Casdoor doesn't have our role yet,
                  // it will be populated on next validateSession call
                  setCurrentUser({ ...result.user, role: role });

                  // Update state
                  setHasValidSession(true);
                  setIsAuthenticated(true);

                  await loginProvider.clearStoredAuthState();

                  resolve({
                    user: result.user,
                    tokens: result.tokens,
                  });
                } catch (error) {
                  console.error("❌ Auth exchange failed:", error);
                  reject(error);
                }
              }
            }
          };

          // Listen for postMessage from callback page
          window.addEventListener("message", handleMessage);

          // Timeout after 5 minutes
          const timeoutId = setTimeout(() => {
            window.removeEventListener("message", handleMessage);
            try {
              if (popup && !popup.closed) popup.close();
            } catch (e) {
              // Ignore COOP errors
            }
            reject(new Error("Login timeout"));
          }, 300000);
        });
      } catch (error) {
        console.error("❌ Popup login failed:", error);
        throw error;
      } finally {
        setAuthLoading(false);
      }
    },
    [loginProvider, postExchange],
  );

  // Simple login function
  const login = useCallback(
    async (): Promise<LoginResult> => {
      return await loginWithPopup();
    },
    [loginWithPopup],
  );

  return {
    // Core edge auth
    edgeAuth,
    hasValidSession,
    buildFnUrl,

    // Independent auth state (account-scoped, NO workspace dependency)
    isAuthenticated,
    currentUser,
    authLoading,

    // Auth actions (same interface as workspace atoms)
    login,
    loginWithPopup,
    handleSignout,
    exchangeJWTForApiKey,
    validateSession,
  };
}
